import React, { useMemo } from 'react';
import { useAuth } from '../components/contexts/AuthContext';
import Navbar from '../components/layout/Navbar';
import EntityTable from '../components/common/EntityTable';
import EntityModal from '../components/common/EntityModal';
import FilterPanel from '../components/common/FilterPanel';
import Pagination from '../components/common/Pagination';
import ConfirmDialog from '../components/common/ConfirmDialog';
import usePaginatedEntity from '../hooks/usePaginatedEntity';
import apiService from '../services/apiService';
import { useExportExcel } from '../hooks/useExportExcel';
import Breadcrumb from '../components/common/Breadcrumb';
import ROUTES from '../constants/routes';

/**
 * Página de gestión de ubicaciones
 *
 * Catálogo de ubicaciones donde pueden estar los equipos
 * (bodegas, talleres, obras, etc.)
 *
 * @returns {JSX.Element}
 */
function Ubicaciones() {
  const { hasPermission } = useAuth();

  const {
    data: ubicaciones,
    rawData,
    loading,
    pagination,
    filters,
    modalOpen,
    editingItem,
    confirmDialog,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    handleCreate,
    handleEdit,
    handleDelete,
    confirmDelete,
    handleSave,
    closeModal,
    closeConfirm
  } = usePaginatedEntity({
    entityName: 'ubicación',
    entityNamePlural: 'ubicaciones',
    fetchFn: apiService.getUbicaciones,
    createFn: apiService.createUbicacion,
    updateFn: apiService.updateUbicacion,
    deleteFn: apiService.deleteUbicacion,
    messages: {
      createSuccess: 'Ubicación creada',
      updateSuccess: 'Ubicación actualizada',
      deleteSuccess: 'Ubicación eliminada'
    }
  });

  const exportToExcel = useExportExcel({
    data: rawData,
    mapFn: (u) => ({ 'Código': u.codigo, 'Nombre': u.nombre, 'Descripción': u.descripcion || '', 'Dirección': u.direccion || '' }),
    sheetName: 'Ubicaciones',
    columnWidths: [{ wch: 12 }, { wch: 25 }, { wch: 35 }, { wch: 30 }],
    entityLabel: 'ubicaciones'
  });

  const columns = useMemo(() => [
    { key: 'codigo', label: 'Código', sortable: true, highlightable: true, title: true },
    { key: 'nombre', label: 'Nombre', sortable: true, highlightable: true },
    { key: 'descripcion', label: 'Descripción', highlightable: true },
    { key: 'direccion', label: 'Dirección', highlightable: true },
    { key: 'acciones', label: 'Acciones', align: 'center' }
  ], []);

  const modalFields = useMemo(() => [
    {
      name: 'codigo',
      label: 'Código',
      type: 'text',
      required: true,
      placeholder: 'Ej: BOD-01, TALL-A, OBRA-NORTE'
    },
    { name: 'nombre', label: 'Nombre', type: 'text', required: true },
    { name: 'descripcion', label: 'Descripción', type: 'textarea' },
    { name: 'direccion', label: 'Dirección', type: 'text', placeholder: 'Dirección física (opcional)' }
  ], []);

  const filterConfig = useMemo(() => [
    {
      name: 'search',
      type: 'text',
      placeholder: 'Buscar por código, nombre o dirección...',
      icon: 'fa-search'
    }
  ], []);

  return (
    <>
      <Navbar />
      <div className="main-content">
        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Ubicaciones' }
        ]} />

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={[
            { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: exportToExcel },
            { label: 'Nueva Ubicación', icon: 'fa-plus', variant: 'primary', onClick: handleCreate, show: () => hasPermission('ubicaciones.create') }
          ]}
        />

        <EntityTable
          columns={columns}
          data={ubicaciones}
          loading={loading}
          searchTerm={filters.search}
          emptyMessage="No se encontraron ubicaciones"
          actions={[
            { icon: 'fa-edit',  className: 'btn-ghost-primary', title: 'Editar',   onClick: handleEdit,   show: () => hasPermission('ubicaciones.update') },
            { icon: 'fa-trash', className: 'btn-ghost-danger',  title: 'Eliminar', onClick: handleDelete, show: () => hasPermission('ubicaciones.delete') },
          ]}
          animateRows={true}
        />

        <Pagination
          currentPage={pagination.page}
          totalItems={pagination.total}
          itemsPerPage={pagination.limit}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleLimitChange}
          loading={loading}
        />

        <EntityModal
          isOpen={modalOpen}
          onClose={closeModal}
          onSave={handleSave}
          title={editingItem ? 'Editar Ubicación' : 'Nueva Ubicación'}
          fields={modalFields}
          initialData={editingItem}
        />

        <ConfirmDialog
          isOpen={confirmDialog.open}
          onClose={closeConfirm}
          onConfirm={confirmDelete}
          title="Eliminar Ubicación"
          message={`¿Está seguro de eliminar la ubicación "${confirmDialog.item?.nombre}"?`}
          confirmText="Eliminar"
          variant="danger"
        />
      </div>
    </>
  );
}

export default Ubicaciones;
